import { useCallback, useRef } from 'react';
import { callBackend } from '../../../utils/api';
import { isFactorContextChangedError } from '../../../utils/factorContextChange';
import { buildFactorMatrixScopePayload, buildLightweightFactorContext, hasReadyWatchlistCogniveRows } from '../dashboardUtils';

const cogniveIdentityKey = (row) => {
    const cogniveId = String(row?.cognive_id ?? '').trim().toLowerCase();
    return cogniveId ? `cognive:${cogniveId}` : null;
};

const metadataFieldsForIndustry = (metadataRow) => {
    const fields = {};
    [
        'in_actual_portfolio',
        'actual_quantity',
        'portfolio_selection_token',
        'portfolio_state_hash',
        'portfolio_editability',
        // Attribute edits also advance the revision bound to removal tokens.
        'delete_group_token',
        'delete_group_control',
        'delete_will_remove_portfolio',
    ].forEach((field) => {
        if (Object.prototype.hasOwnProperty.call(metadataRow || {}, field)) {
            fields[field] = metadataRow[field];
        }
    });
    [
        'company_name',
        'inbound_id_type',
        'inbound_id_value',
        'msi_listing_label',
        'uploaded_ticker',
        'yf_longname',
        'yf_shortname',
    ].forEach((field) => {
        const value = metadataRow?.[field];
        if (value != null && (typeof value !== 'string' || value.trim() !== '')) {
            fields[field] = value;
        }
    });
    return fields;
};

export const mergeWatchlistMetadataRows = (
    currentRows = [],
    metadataRows = [],
) => {
    const metadataByIdentity = new Map();
    (Array.isArray(metadataRows) ? metadataRows : []).forEach((row) => {
        const key = cogniveIdentityKey(row);
        if (key && !metadataByIdentity.has(key)) {
            metadataByIdentity.set(key, row);
        }
    });

    return (Array.isArray(currentRows) ? currentRows : []).map((row) => {
        const key = cogniveIdentityKey(row);
        const metadataRow = key ? metadataByIdentity.get(key) : null;
        return metadataRow
            ? { ...row, ...metadataFieldsForIndustry(metadataRow) }
            : row;
    });
};

const watchlistRevisionFromResponse = (response) => {
    const hasRevisionId = Object.prototype.hasOwnProperty.call(
        response || {},
        'watchlist_revision_id',
    );
    const hasRevisionSeq = Object.prototype.hasOwnProperty.call(
        response || {},
        'watchlist_revision_seq',
    );
    if (!hasRevisionId || !hasRevisionSeq) {
        throw new Error('Watchlist response is missing its accepted revision stamp.');
    }
    const revisionId = String(response?.watchlist_revision_id || '').trim();
    const rawRevisionSeq = response?.watchlist_revision_seq;
    if (!revisionId && rawRevisionSeq == null) {
        return { revisionId: null, revisionSeq: 0 };
    }
    const revisionSeq = Number(rawRevisionSeq);
    if (!revisionId || !Number.isSafeInteger(revisionSeq) || revisionSeq <= 0) {
        throw new Error('Watchlist response contains an invalid accepted revision stamp.');
    }
    return { revisionId, revisionSeq };
};

export default function usePortfolioBaseDataFetchers({
    userUid,
    actualPortfolioId,
    enablePositionsFetch,
    enableIndustriesFetch,
    selectedFactorModel,
    selectedBetaSource,
    factorSnapshotDate,
    factorDataModel,
    currentIdioVolSource,
    idioVolSource,
    setActualPortfolioId,
    setActualPortfolioIsReady,
    setUserPositions,
    setPositions,
    setPositionsLoading,
    setIndustries,
    setWatchlistAttributeSnapshot,
    setWatchlistPortfolioEditor,
    setIndustriesLoading,
    setWatchlistIsReady,
    setError,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarOpen,
}) {
    const positionsRequestIdRef = useRef(0);
    const industriesRequestIdRef = useRef(0);
    const readyIndustrySnapshotRef = useRef(null);

    const clearPositionsForEmptyUniverse = useCallback(() => {
        positionsRequestIdRef.current += 1;
        setUserPositions([]);
        setPositions([]);
        setPositionsLoading(false);
        setActualPortfolioIsReady(false);
    }, [
        setActualPortfolioIsReady,
        setPositions,
        setPositionsLoading,
        setUserPositions,
    ]);

    const fetchActualPortfolioId = useCallback(async ({
        isCurrentRequest = null,
        stagePublicationState = null,
        stagePresentationState = null,
    } = {}) => {
        if (!userUid) return undefined;
        const isCurrent = typeof isCurrentRequest === 'function'
            ? isCurrentRequest
            : () => true;
        if (!isCurrent()) return undefined;
        const present = apply => { if (!stagePresentationState) apply(); };
        try {
            const result = await callBackend('fetch_actual_portfolio_id', 'POST', {});
            const fetchedId = result?.actual_portfolio_id ?? null;
            if (!isCurrent()) {
                return undefined;
            }
            const publishActualPortfolioId = () => setActualPortfolioId(fetchedId);
            if (typeof stagePublicationState === 'function') {
                stagePublicationState(publishActualPortfolioId, isCurrent);
            } else {
                publishActualPortfolioId();
            }
            return fetchedId;
        } catch (error) {
            if (!isCurrent()) {
                return undefined;
            }
            console.error('Error fetching actual portfolio id:', error);
            present(() => setError('Failed to load actual portfolio.'));
            present(() => setSnackbarMessage('Failed to load actual portfolio.'));
            present(() => setSnackbarSeverity('error'));
            present(() => setSnackbarOpen(true));
            throw error;
        }
    }, [
        setActualPortfolioId,
        setError,
        setSnackbarMessage,
        setSnackbarOpen,
        setSnackbarSeverity,
        userUid,
    ]);

    const fetchPositions = useCallback(async (portfolioId = null, options = {}) => {
        if (!enablePositionsFetch || !userUid) {
            return { userPositions: [], portfolioId: null };
        }
        const isCurrentRequest = typeof options?.isCurrentRequest === 'function'
            ? options.isCurrentRequest
            : () => true;
        if (!isCurrentRequest()) {
            return { userPositions: [], portfolioId: null, stale: true };
        }
        const stagePublicationState = options?.stagePublicationState;
        if (typeof stagePublicationState !== 'function') {
            throw new Error('Positions publication transaction is required.');
        }
        const requestedPortfolioId = portfolioId ?? actualPortfolioId;
        const silent = options?.silent === true;
        const riskContextOverride = options?.riskContextOverride;
        const requestId = silent
            ? positionsRequestIdRef.current
            : ++positionsRequestIdRef.current;
        const isCurrentPositionsRequest = () => (
            isCurrentRequest()
            && positionsRequestIdRef.current === requestId
        );
        const present = apply => { if (!options.stagePresentationState) apply(); };
        let startedLoading = false;
        if (!silent) {
            present(() => setPositionsLoading(true));
            startedLoading = true;
        }

        try {
            const requestPayload = riskContextOverride == null
                ? { portfolio_id: requestedPortfolioId }
                : {
                    portfolio_id: requestedPortfolioId,
                    factorModel: riskContextOverride.factorModel,
                    factor_data_model: riskContextOverride.factorDataModel,
                    factor_snapshot_date: riskContextOverride.factorSnapshotDate,
                    beta_source: riskContextOverride.betaSource,
                    idio_vol_source: riskContextOverride.idioVolSource,
                };
            const result = await callBackend('fetch_positions', 'POST', requestPayload);
            if (!isCurrentPositionsRequest()) {
                return {
                    userPositions: [],
                    portfolioId: null,
                    risk_calculation_coverage: null,
                    stale: true,
                };
            }
            if (
                !result
                || !Array.isArray(result.user_positions)
                || !Array.isArray(result.positions)
            ) {
                throw new Error('Invalid data structure returned from fetch_positions function.');
            }
            const newUserPositions = result.user_positions;
            const targetPortfolioId = requestedPortfolioId ?? result?.portfolio_id ?? null;
            const publishPositions = () => {
                if (options.stagePresentationState) setPositionsLoading(false);
                setUserPositions(newUserPositions);
                setPositions(result.positions);
                if (newUserPositions.length > 0 && targetPortfolioId != null) {
                    setActualPortfolioIsReady(true);
                } else {
                    setActualPortfolioIsReady(false);
                }
            };
            const publicationResult = {
                loaded: true,
                userPositions: newUserPositions,
                positions: result.positions,
                portfolioId: targetPortfolioId,
                risk_calculation_coverage: result?.risk_calculation_coverage ?? null,
            };
            stagePublicationState(
                publishPositions,
                isCurrentPositionsRequest,
                Number.isInteger(options?.generation) && options?.resourceScopeKey
                    ? {
                        panel: 'positions',
                        resource: publicationResult,
                        scopeKey: options.resourceScopeKey,
                        generation: options.generation,
                    }
                    : undefined,
            );
            return publicationResult;
        } catch (error) {
            if (!isCurrentPositionsRequest()) {
                return {
                    userPositions: [],
                    portfolioId: null,
                    risk_calculation_coverage: null,
                    stale: true,
                };
            }
            console.error('Error fetching positions:', error);
            // A superseded factor context is followed, not reported.
            if (!silent && !isFactorContextChangedError(error)) {
                present(() => setError('Failed to load positions. Please try again.'));
                present(() => setSnackbarMessage('Failed to load positions.'));
                present(() => setSnackbarSeverity('error'));
                present(() => setSnackbarOpen(true));
            }
            throw error;
        } finally {
            if (
                startedLoading
                && positionsRequestIdRef.current === requestId
            ) {
                present(() => setPositionsLoading(false));
            }
        }
    }, [
        actualPortfolioId,
        enablePositionsFetch,
        setActualPortfolioIsReady,
        setError,
        setPositions,
        setPositionsLoading,
        setSnackbarMessage,
        setSnackbarOpen,
        setSnackbarSeverity,
        setUserPositions,
        userUid,
    ]);

    const applyIndustryRows = useCallback((
        rows = [],
        portfolioEditor = null,
        attributeSnapshot = null,
    ) => {
        const normalizedRows = Array.isArray(rows) ? rows : [];
        setIndustries(normalizedRows);
        setWatchlistAttributeSnapshot?.(attributeSnapshot);
        setWatchlistPortfolioEditor?.(portfolioEditor);
        setWatchlistIsReady(hasReadyWatchlistCogniveRows(normalizedRows));
    }, [
        setIndustries,
        setWatchlistAttributeSnapshot,
        setWatchlistIsReady,
        setWatchlistPortfolioEditor,
    ]);

    const fetchIndustries = useCallback(async (
        matricesOverride = null,
        {
            isCurrentRequest = null,
            requireMatrixBackedRows = false,
            betaSource = null,
            stagePublicationState = null,
            metadataOnly = false,
            authoritativeEmpty = false,
            expectedWatchlistRevisionId = null,
            expectedWatchlistRevisionSeq = null,
            stagePresentationState = null,
        } = {},
    ) => {
        if (!enableIndustriesFetch || !userUid) return;
        const isCurrent = typeof isCurrentRequest === 'function'
            ? isCurrentRequest
            : () => true;
        if (!isCurrent()) {
            return undefined;
        }
        const {
            factorModel: resolvedFactorModel,
            factorDataModel: resolvedFactorDataModel,
            factorSnapshotDate: snapshotDate,
            idioVolSource: resolvedIdioVolSource,
            betaSource: resolvedBetaSource,
        } = buildLightweightFactorContext({
            factorModel: selectedFactorModel,
            factorDataModel,
            factorSnapshotDate,
            idioVolSource: currentIdioVolSource ?? idioVolSource,
            betaSource: betaSource ?? selectedBetaSource,
            override: matricesOverride,
        });
        const matrixScopePayload = buildFactorMatrixScopePayload(matricesOverride);
        const riskContextKey = JSON.stringify([
            userUid, resolvedFactorModel, snapshotDate, resolvedFactorDataModel,
            resolvedIdioVolSource, resolvedBetaSource, matrixScopePayload,
        ]);
        const expectedRevisionId = String(expectedWatchlistRevisionId || '').trim();
        const expectedRevisionSeq = Number(expectedWatchlistRevisionSeq);
        const requiresExactPublicationRevision = Boolean(expectedRevisionId)
            && Number.isSafeInteger(expectedRevisionSeq)
            && expectedRevisionSeq > 0;

        const requireExactPublicationRevision = (watchlistRevision) => {
            if (!requiresExactPublicationRevision) return;
            if (
                watchlistRevision?.revisionId !== expectedRevisionId
                || watchlistRevision?.revisionSeq !== expectedRevisionSeq
            ) {
                const publicationError = new Error(
                    'The published Watchlist revision is not readable yet.',
                );
                publicationError.code = 'watchlist_rows_not_ready';
                publicationError.retryable = true;
                throw publicationError;
            }
        };

        const requestId = ++industriesRequestIdRef.current;
        const present = apply => { if (!stagePresentationState) apply(); };
        let startedLoading = false;
        present(() => setIndustriesLoading(true));
        startedLoading = true;
        const isCurrentIndustryRequest = () => (
            isCurrent()
            && industriesRequestIdRef.current === requestId
        );

        const fetchWatchlistMetadataFallback = async (reason = null, preserveVerifiedRisk = false) => {
            const watchlistResp = await callBackend('fetch_watchlist_info', 'POST', {
                beta_source: resolvedBetaSource,
                factorModel: resolvedFactorModel,
                factor_snapshot_date: snapshotDate,
                factor_data_model: resolvedFactorDataModel,
                include_waiting_market_data: true,
                // Trailing returns are market data: shown without factor data too.
                include_trailing_returns: true,
            });
            if (!isCurrentIndustryRequest()) {
                return undefined;
            }
            if (!Array.isArray(watchlistResp?.data)) {
                throw new Error('Invalid data structure returned from fetch_watchlist_info.');
            }
            const watchlistRevision = watchlistRevisionFromResponse(watchlistResp);
            requireExactPublicationRevision(watchlistRevision);
            const fallbackReason = reason || null;
            let watchlistRows = watchlistResp.data.map((row) => ({
                    ...row,
                    ...(fallbackReason ? {
                        _matrixBackedRiskLoaded: false,
                        _riskDataFallbackReason: fallbackReason,
                        _riskDataStatus: 'metadata_fallback',
                    } : {}),
                }));

            const ready = readyIndustrySnapshotRef.current;
            if (preserveVerifiedRisk
                && matrixScopePayload.matrix_scope_version_key
                && fallbackReason === 'risk_rows_unavailable'
                && watchlistResp.watchlist_factor_context_id
                && ready?.factorContextId === watchlistResp.watchlist_factor_context_id
                && ready?.riskContextKey === riskContextKey
                && ready.watchlistRevision.revisionId === watchlistRevision.revisionId
                && ready.watchlistRevision.revisionSeq === watchlistRevision.revisionSeq
                && ready.rows.length === watchlistRows.length
                && watchlistRows.every((row) => ready.identities.has(row.cognive_id))) {
                watchlistRows = mergeWatchlistMetadataRows(ready.rows, watchlistResp.data);
            }
            const publishIndustryRows = () => {
                if (stagePresentationState) setIndustriesLoading(false);
                applyIndustryRows(watchlistRows, watchlistResp?.portfolio_editor ?? null,
                    watchlistResp?.watchlist_attribute_snapshot ?? null);
            };
            if (typeof stagePublicationState === 'function') {
                stagePublicationState(publishIndustryRows, isCurrentIndustryRequest);
            } else {
                publishIndustryRows();
            }
            return {
                rows: watchlistRows,
                watchlistRevision,
            };
        };

        try {
            if (authoritativeEmpty) {
                const response = await fetchWatchlistMetadataFallback();
                if (response && response.rows.length !== 0) {
                    const publicationError = new Error(
                        'The published Watchlist is not empty yet.',
                    );
                    publicationError.code = 'watchlist_rows_not_ready';
                    publicationError.retryable = true;
                    throw publicationError;
                }
                return response;
            }
            if (metadataOnly) {
                return await fetchWatchlistMetadataFallback('matrix_unavailable');
            }
            const result = await callBackend(
                'fetch_industries', 'POST', {
                    factorModel: resolvedFactorModel,
                    beta_source: resolvedBetaSource,
                    factor_snapshot_date: snapshotDate,
                    factor_data_model: resolvedFactorDataModel,
                    idio_vol_source: resolvedIdioVolSource,
                    ...matrixScopePayload,
                    ...(requiresExactPublicationRevision ? {
                        expected_watchlist_revision_id: expectedRevisionId,
                        expected_watchlist_revision_seq: expectedRevisionSeq,
                    } : {}),
                }
            );
            if (!isCurrentIndustryRequest()) {
                return undefined;
            }
            if (Array.isArray(result?.industries)) {
                const watchlistRevision = watchlistRevisionFromResponse(result);
                requireExactPublicationRevision(watchlistRevision);
                if (
                    requireMatrixBackedRows
                    && matricesOverride
                    && result.industries.length === 0
                ) {
                    throw new Error(
                        'Matrix-backed watchlist rows were empty for a non-empty factor context.',
                    );
                }
                const publishIndustryRows = () => {
                    if (stagePresentationState) setIndustriesLoading(false);
                    applyIndustryRows(
                        result.industries,
                        result?.portfolio_editor ?? null,
                        result?.watchlist_attribute_snapshot ?? null,
                    );
                    readyIndustrySnapshotRef.current = matrixScopePayload.matrix_scope_version_key
                        && result.watchlist_factor_context_id ? {
                        factorContextId: result.watchlist_factor_context_id,
                        riskContextKey, watchlistRevision, rows: result.industries,
                        identities: new Set(result.industries.map((row) => row.cognive_id)),
                    } : null;
                };
                if (typeof stagePublicationState === 'function') {
                    stagePublicationState(publishIndustryRows, isCurrentIndustryRequest);
                } else {
                    publishIndustryRows();
                }
                return {
                    rows: result.industries,
                    watchlistRevision,
                    ...(result.watchlist_factor_context_id ? {
                        watchlistFactorContextId: result.watchlist_factor_context_id,
                    } : {}),
                };
            } else {
                present(() => setError('Invalid data structure returned from fetch_industries function.'));
                present(() => setSnackbarMessage('Failed to load industries.'));
                present(() => setSnackbarSeverity('error'));
                present(() => setSnackbarOpen(true));
                throw new Error('Invalid data structure returned from fetch_industries function.');
            }

        } catch (error) {
            if (!isCurrentIndustryRequest()) {
                return undefined;
            }
            if (requireMatrixBackedRows) {
                if (requiresExactPublicationRevision) {
                    if (error && typeof error === 'object') {
                        error.code = error.code || 'watchlist_rows_not_ready';
                        error.retryable = error.retryable !== false;
                    }
                    throw error;
                }
                console.warn('Matrix-backed watchlist rows are unavailable:', error);
                present(() => setError('Failed to load matrix-backed watchlist rows. Please try again.'));
                present(() => setSnackbarMessage('Failed to load industries.'));
                present(() => setSnackbarSeverity('error'));
                present(() => setSnackbarOpen(true));
                return undefined;
            }
            console.warn('Risk-backed industries unavailable; falling back to watchlist metadata:', error);
            try {
                return await fetchWatchlistMetadataFallback(
                    'risk_rows_unavailable', error instanceof TypeError,
                );
            } catch (fallbackError) {
                if (!isCurrentIndustryRequest()) {
                    return undefined;
                }
                console.error('Error fetching industries:', fallbackError);
                present(() => setError('Failed to load industries. Please try again.'));
                present(() => setSnackbarMessage('Failed to load industries.'));
                present(() => setSnackbarSeverity('error'));
                present(() => setSnackbarOpen(true));
            }
            return undefined;
        } finally {
            if (
                startedLoading
                && industriesRequestIdRef.current === requestId
            ) {
                present(() => setIndustriesLoading(false));
            }
        }
    }, [
        applyIndustryRows,
        enableIndustriesFetch,
        factorDataModel,
        factorSnapshotDate,
        currentIdioVolSource,
        idioVolSource,
        selectedBetaSource,
        selectedFactorModel,
        setError,
        setIndustriesLoading,
        setSnackbarMessage,
        setSnackbarOpen,
        setSnackbarSeverity,
        userUid,
    ]);

    const refreshIndustryMetadata = useCallback(async ({
        isCurrentRequest = null,
    } = {}) => {
        if (!enableIndustriesFetch || !userUid) return undefined;
        const isCurrent = typeof isCurrentRequest === 'function'
            ? isCurrentRequest
            : () => true;
        if (!isCurrent()) return undefined;

        const requestId = ++industriesRequestIdRef.current;
        const isCurrentIndustryRequest = () => (
            isCurrent()
            && industriesRequestIdRef.current === requestId
        );
        try {
            const watchlistResp = await callBackend('fetch_watchlist_info', 'POST', {
                beta_source: selectedBetaSource,
                factorModel: selectedFactorModel,
                factor_snapshot_date: factorSnapshotDate,
                factor_data_model: factorDataModel,
                include_waiting_market_data: true,
            });
            if (!isCurrentIndustryRequest()) {
                return undefined;
            }

            if (!Array.isArray(watchlistResp?.data)) {
                throw new Error('Invalid data structure returned from fetch_watchlist_info.');
            }
            const metadataRows = watchlistResp.data;
            setIndustries((currentRows) => mergeWatchlistMetadataRows(
                currentRows,
                metadataRows,
            ));
            setWatchlistAttributeSnapshot?.(
                watchlistResp?.watchlist_attribute_snapshot ?? null,
            );
            setWatchlistPortfolioEditor?.(watchlistResp?.portfolio_editor ?? null);
            return metadataRows;
        } catch (error) {
            if (!isCurrentIndustryRequest()) {
                return undefined;
            }
            console.error('Error refreshing watchlist metadata:', error);
            setError('Failed to refresh watchlist details. Please try again.');
            setSnackbarMessage('Failed to refresh watchlist details.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return undefined;
        } finally {
            if (industriesRequestIdRef.current === requestId) {
                setIndustriesLoading(false);
            }
        }
    }, [
        enableIndustriesFetch,
        factorDataModel,
        factorSnapshotDate,
        selectedBetaSource,
        selectedFactorModel,
        setError,
        setIndustries,
        setIndustriesLoading,
        setWatchlistAttributeSnapshot,
        setWatchlistPortfolioEditor,
        setSnackbarMessage,
        setSnackbarOpen,
        setSnackbarSeverity,
        userUid,
    ]);

    return {
        fetchActualPortfolioId,
        clearPositionsForEmptyUniverse,
        fetchPositions,
        fetchIndustries,
        refreshIndustryMetadata,
    };
}
